import React, { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AccountService, { ProfileData, AuditEntry } from '../../../services/account.service';
import AvatarUpload from '../../../components/AvatarUpload';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import { AuthContext } from '../../../contexts/AuthContext';

const TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Toronto', 'America/Sao_Paulo', 'America/Argentina/Buenos_Aires',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid', 'Europe/Rome',
  'Europe/Amsterdam', 'Europe/Stockholm', 'Europe/Warsaw', 'Europe/Athens',
  'Europe/Istanbul', 'Europe/Moscow', 'Asia/Dubai', 'Asia/Karachi', 'Asia/Kolkata',
  'Asia/Dhaka', 'Asia/Bangkok', 'Asia/Shanghai', 'Asia/Tokyo', 'Asia/Seoul',
  'Asia/Singapore', 'Asia/Jakarta', 'Asia/Taipei', 'Asia/Manila',
  'Australia/Sydney', 'Australia/Melbourne', 'Pacific/Auckland', 'Pacific/Honolulu',
  'Africa/Cairo', 'Africa/Lagos', 'Africa/Nairobi', 'Africa/Johannesburg',
];

interface Props {
  profile: ProfileData | null;
  onProfileUpdated: () => void;
}

const ProfileTab: React.FC<Props> = ({ profile, onProfileUpdated }) => {
  const { t } = useTranslation();
  const { setLoggedInUser } = useContext(AuthContext);
  const [form, setForm] = useState({
    full_name: '',
    phone: '',
    timezone: 'UTC',
    language: 'en',
    theme: 'light',
  });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activity, setActivity] = useState<AuditEntry[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  useUnsavedChanges(dirty);

  useEffect(() => {
    if (!profile) return;
    setForm({
      full_name: profile.full_name ?? '',
      phone: profile.phone ?? '',
      timezone: profile.timezone ?? 'UTC',
      language: profile.language ?? 'en',
      theme: profile.theme ?? 'light',
    });
    setDirty(false);
  }, [profile]);

  useEffect(() => {
    setActivityLoading(true);
    AccountService.getActivity(10)
      .then((res: any) => setActivity(res.data ?? []))
      .catch(() => setActivity([]))
      .finally(() => setActivityLoading(false));
  }, []);

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await AccountService.updateProfile(form as any);
      setDirty(false);
      toast.success(t('account.profile.saved') as string);
      setLoggedInUser((prev: any) => ({
        ...prev,
        full_name: form.full_name,
        phone: form.phone,
        timezone: form.timezone,
        language: form.language,
      }));
      onProfileUpdated();
    } catch {
      toast.error(t('account.profile.saveError') as string);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: profile?.timezone || 'UTC',
    });

  return (
    <div className="d-flex flex-column gap-4">
      {/* Profile card */}
      <div className="card" style={{ borderRadius: '0.75rem' }}>
        <div className="card-header border-bottom pb-3">
          <h6 className="mb-0 fw-semibold">{t('account.profile.title')}</h6>
        </div>
        <div className="card-body">
          <div className="d-flex align-items-center gap-4 mb-4">
            <AvatarUpload
              name={profile?.full_name ?? profile?.email ?? 'User'}
              currentUrl={profile?.avatar_url ?? null}
              onSuccess={() => { toast.success(t('account.profile.avatarUpdated') as string); onProfileUpdated(); }}
            />
            <div>
              <div className="fw-semibold">{profile?.full_name ?? profile?.email}</div>
              <div className="text-muted small">{profile?.email}</div>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-12 col-sm-6">
              <label className="form-label small fw-medium">{t('account.profile.fullName')}</label>
              <input
                className="form-control"
                value={form.full_name}
                onChange={(e) => handleChange('full_name', e.target.value)}
                placeholder={t('account.profile.fullNamePlaceholder')}
              />
            </div>
            <div className="col-12 col-sm-6">
              <label className="form-label small fw-medium">{t('account.profile.phone')}</label>
              <input
                className="form-control"
                value={form.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder={t('account.profile.phonePlaceholder')}
              />
            </div>
            <div className="col-12 col-sm-6">
              <label className="form-label small fw-medium">{t('account.profile.timezone')}</label>
              <select
                className="form-select"
                value={form.timezone}
                onChange={(e) => handleChange('timezone', e.target.value)}
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-6">
              <label className="form-label small fw-medium">{t('account.profile.language')}</label>
              <select
                className="form-select"
                value={form.language}
                onChange={(e) => handleChange('language', e.target.value)}
              >
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </div>
          </div>

          <div className="mt-4 d-flex justify-content-end">
            <button
              className="btn btn-primary px-4"
              onClick={handleSave}
              disabled={!dirty || saving}
            >
              {saving
                ? <span className="spinner-border spinner-border-sm me-2" />
                : null}
              {t('common.save')}
            </button>
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <div className="card" style={{ borderRadius: '0.75rem' }}>
        <div className="card-header border-bottom pb-3">
          <h6 className="mb-0 fw-semibold">{t('account.activity.title')}</h6>
        </div>
        <div className="card-body p-0">
          {activityLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm text-primary" />
            </div>
          ) : activity.length === 0 ? (
            <div className="text-center py-4 text-muted small">{t('account.activity.empty')}</div>
          ) : (
            <ul className="list-group list-group-flush">
              {activity.map((entry) => (
                <li key={entry.id} className="list-group-item d-flex align-items-start gap-3 py-3 px-4">
                  <i className="ri-shield-check-line ri-20px text-muted mt-1" />
                  <div className="flex-grow-1">
                    <div className="small fw-medium">{entry.action_description}</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      {formatDate(entry.created_at)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileTab;
